import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { JobStatus } from '@stamp/merch';
import { StatusBadge } from '../components/StatusBadge';
import {
  fetchJobs,
  type Job,
  statusLabel,
  ALL_STATUSES,
} from '../api';

const STATUS_FILTERS: ('all' | JobStatus)[] = ['all', ...ALL_STATUSES];

export function Dashboard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | JobStatus>('all');

  useEffect(() => {
    fetchJobs().then((j) => {
      setJobs(j);
      setLoading(false);
    });
  }, []);

  const filtered = filter === 'all' ? jobs : jobs.filter((j) => j.status === filter);

  const stats = {
    total: jobs.length,
    active: jobs.filter((j) => !['published', 'rejected', 'archived'].includes(j.status)).length,
    published: jobs.filter((j) => j.status === 'published').length,
    review: jobs.filter((j) => ['artwork_review', 'merch_review'].includes(j.status)).length,
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0 }}>Dashboard</h1>
        <button className="btn btn--primary" onClick={() => navigate('/new')}>
          + New Design
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <p className="stat-card__label">Total Jobs</p>
          <p className="stat-card__value">{stats.total}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Active</p>
          <p className="stat-card__value">{stats.active}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">In Review</p>
          <p className="stat-card__value">{stats.review}</p>
        </div>
        <div className="stat-card">
          <p className="stat-card__label">Published</p>
          <p className="stat-card__value">{stats.published}</p>
        </div>
      </div>

      <div className="filter-bar">
        <span className="filter-bar__label">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            className={`filter-chip ${filter === s ? 'filter-chip--active' : ''}`}
            onClick={() => setFilter(s)}
          >
            {s === 'all' ? 'All' : statusLabel(s as JobStatus)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">&#128188;</div>
          <div className="empty-state__title">No jobs found</div>
          <p>No jobs match this filter. Try a different status or create a new design.</p>
        </div>
      ) : (
        <div className="job-list">
          {filtered.map((job) => (
            <div key={job.id} className="job-item" onClick={() => navigate(`/job/${job.id}`)}>
              <div className="job-item__id">{job.id}</div>
              <div className="job-item__meta">
                <div className="job-item__name">{job.brief.logoPrompt}</div>
                <div className="job-item__brand">{job.brand}</div>
              </div>
              <StatusBadge status={job.status} />
              <div className="job-item__date">
                {new Date(job.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
